/**
 * Who may open which portal page on a site (ORIENTATION-ROLES P2).
 *
 * Mirrors the module's M4 gates over the caller's own rows:
 *   - author: orientation admin (WorkOS `admin` or `orientation-admin`), or an
 *     active `user_site` row plus a `site_right` row with role Writer or
 *     Admin, or Allow authoring;
 *   - site admin: orientation admin, or an active row plus role Admin;
 *   - site role picker and invite (D9): the site's Admin (TurboTwin
 *     ProjectAdmin) or WorkOS `admin`, never an orientation admin;
 *   - org settings (project agent, mp config): WorkOS `admin` only.
 * `orgviewer` adds nothing here (D10): trainee pages unless a site right
 * says more. A `site_right` row without an active membership grants nothing.
 *
 * Pure; tested in siteAccess.test.ts.
 */
import type { PortalRole } from '@/hooks/useRole';
import type { ViewKey } from '@/data/types';

export type SiteRightLike = { projectId: string; role: string; allowAuthoring: boolean };
export type SiteMembershipLike = { projectId: string; active: boolean };

export type SiteAccess = {
  /** Orientations, Orientation Editor. */
  author: boolean;
  /** People, assignments, cohorts, Allow authoring. */
  siteAdmin: boolean;
  /** Site role picker and invite (D9). */
  manageRoles: boolean;
  /** WorkOS `admin`: org settings, Orientation admin switch. */
  orgAdmin: boolean;
};

export function isOrientationAdmin(role: PortalRole): boolean {
  return role === 'admin' || role === 'orientation-admin';
}

export function siteAccessFor(
  role: PortalRole,
  projectId: string,
  mySites: readonly SiteMembershipLike[],
  myRights: readonly SiteRightLike[],
): SiteAccess {
  const orgAdmin = role === 'admin';
  const orgWide = isOrientationAdmin(role);
  const member = !!projectId && mySites.some((s) => s.projectId === projectId && s.active);
  const right = member ? myRights.find((r) => r.projectId === projectId) : undefined;
  const siteRoleAdmin = right?.role === 'Admin';
  const writes = siteRoleAdmin || right?.role === 'Writer' || !!right?.allowAuthoring;
  return {
    author: orgWide || writes,
    siteAdmin: orgWide || siteRoleAdmin,
    manageRoles: orgAdmin || siteRoleAdmin,
    orgAdmin,
  };
}

const AUTHOR_VIEWS: readonly ViewKey[] = ['orientations', 'orientation-editor'];
const SITE_ADMIN_VIEWS: readonly ViewKey[] = [
  'workforce',
  'members',
  'cohorts',
  'cohort-builder',
  'review-queue',
  'live',
  'leadership',
];

/** Whether this access opens the page. Unknown views are not opened. */
export function canOpenView(view: ViewKey, a: SiteAccess): boolean {
  if (view === 'trainee' || view === 'settings') return true;
  if (AUTHOR_VIEWS.includes(view)) return a.author;
  if (SITE_ADMIN_VIEWS.includes(view)) return a.siteAdmin;
  return false;
}

/** The default page: Leadership for a site admin, Orientations for an author. */
export function landingView(a: SiteAccess): ViewKey {
  if (a.siteAdmin) return 'leadership';
  if (a.author) return 'orientations';
  return 'trainee';
}

/** Allow authoring only means something for a Reader (D8); a site admin sets it (D7). */
export function showAllowAuthoring(siteRole: string, viewerIsSiteAdmin: boolean): boolean {
  return viewerIsSiteAdmin && siteRole === 'Reader';
}

export const ALLOW_AUTHORING_HINT =
  'Can build and edit orientations on this site. Stays read-only in TurboTwin.';

/**
 * The Orientation admin switch (P3) moves a person only between `member` and
 * `orientation-admin`, so it shows to an org admin only on those rows, read
 * from the module tier. Turning it off on an `orgviewer` or a stopgap
 * `site-admin` row would set `member` and drop that role; `set-member-role`
 * refuses that too.
 */
export function showOrientationAdminSwitch(viewerIsWorkosAdmin: boolean, appRoleTag: string | undefined): boolean {
  return viewerIsWorkosAdmin && (appRoleTag === 'Member' || appRoleTag === 'OrientationAdmin');
}
